<template>
  <div class="substep-wrapper">
    <div class="substep" :class="['subitem-5']">
      <h1 class="title">Finalize Your Details</h1>
      <p class="description">
        <span class="desktop"
          >Confirm your interest rate type and repayment term on the left, along with your repayment
          type below.
          <b class="disclaimer"
            >Please note, once final documents have been provided and generated, changes to these
            selections can not be made.</b
          ></span
        >

        <span class="mobile"
          >Confirm your interest rate type, repayment term, and repayment type below.
          <b class="disclaimer"
            >Please note, once your final loan documents are generated, these selections cannot be
            changed.</b
          ></span
        >
      </p>

      <div class="substep-content">
        <div class="sections">
          <div class="column">
            <div>
              <h5 class="section-title">Interest Rate Type</h5>
              <div class="buttons">
                <button
                  v-for="rateType in confirmationSummary.rateTypes"
                  :key="rateType.rateTypeId"
                  class="option-button"
                  :class="[
                    {
                      active: rateTypeSelectionRef?.isActive(rateType.rateTypeId),
                      'default-btn': !rateTypeSelectionRef?.isActive(rateType.rateTypeId),
                    },
                  ]"
                >
                  <h5 class="button-text">{{ rateType.rateTypeName }}</h5>
                </button>
              </div>
            </div>
            <div>
              <h5 class="section-title">Repayment Term</h5>
              <div class="buttons" id="repayment-terms">
                <button
                  v-for="repaymentTermOption in repaymentTermSelectionRef?.repaymentTermOptions"
                  :key="repaymentTermOption.repaymentTermId"
                  class="option-button"
                  :class="[
                    {
                      active: repaymentTermSelectionRef?.isActive(
                        repaymentTermOption.repaymentTermId,
                      ),
                      'default-btn': !repaymentTermSelectionRef?.isActive(
                        repaymentTermOption.repaymentTermId,
                      ),
                    },
                  ]"
                >
                  <h5 class="button-text">{{ repaymentTermOption.repaymentTerm }}</h5>
                </button>
              </div>
            </div>
          </div>
          <div class="column" style="flex: 1">
            <div>
              <h5 class="section-title">Repayment Type</h5>
              <div class="buttons" id="repayment-types" style="">
                <RepaymentTypeButton
                  :readonly="true"
                  v-for="repaymentTypeOption in confirmationSummary.repaymentTypes"
                  :key="repaymentTypeOption?.repaymentTypeId || ''"
                  :repaymentTypeOption="repaymentTypeOption"
                  :selectedRepaymentTypeId="confirmationSummary.selectedRepaymentTypeId"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="substep-footer">
        <div class="button-container" id="button-container">
          <button class="previous-btn" @click="emit('previous')" :disabled="savingChanges">
            <span class="button-text"><span class="button-text">Back</span></span>
          </button>
          <next-button
            class="next-btn"
            @click="handleSubmit"
            :loading="savingChanges"
            text="Complete"
          >
            <span class="button-text">Complete</span>
          </next-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import RepaymentTermSelection from "@/components/professional-grad/loan-configuration/RepaymentTermSelection.vue";
import RateTypeSelection from "@/components/professional-grad/loan-configuration/RateTypeSelection.vue";
import RepaymentTypeSelection from "@/components/professional-grad/loan-configuration/RepaymentTypeSelection.vue";
import RepaymentTypeButton from "@/components/professional-grad/loan-configuration/RepaymentTypeButton.vue";
import NextButton from "@/components/shared/NextButton.vue";

const props = defineProps<{
  rateTypeSelectionRef?: InstanceType<typeof RateTypeSelection> | null;
  repaymentTermSelectionRef?: InstanceType<typeof RepaymentTermSelection> | null;
  repaymentTypeSelectionRef?: InstanceType<typeof RepaymentTypeSelection> | null;
  isCurrent: boolean;
}>();

const emit = defineEmits<{
  (e: "previous"): void;
  (e: "complete"): void;
}>();

const confirmationSummary = computed(() => {
  return {
    rateTypes: props.rateTypeSelectionRef?.rateTypes,
    repaymentTerms: props.repaymentTermSelectionRef?.repaymentTermOptions,
    repaymentTypes: props.repaymentTypeSelectionRef?.repaymentTypeOptions,
    selectedRateTypeId: props.rateTypeSelectionRef?.selectedRateTypeId,
    selectedRepaymentTermId: props.repaymentTermSelectionRef?.selectedRepaymentTermId,
    selectedRepaymentTypeId: props.repaymentTypeSelectionRef?.selectedRepaymentTypeId,
    selectedRatePlanId: props.repaymentTypeSelectionRef?.selectedRatePlan?.id,
  };
});

const savingChanges = ref<boolean>(false);

function handleSubmit() {
  savingChanges.value = true;
  emit("complete");
}

function setSavingChanges(flag: boolean) {
  savingChanges.value = flag;
}

defineExpose({
  savingChanges,
  setSavingChanges,
});
</script>
<style scoped src="@/styles/professional-grad/button.css"></style>
<style scoped src="@/styles/professional-grad/substep.css"></style>
<style scoped src="@/styles/professional-grad/substep-5.css"></style>
